<template>
  <div>
    <card-container
      :id="pageId"
      :is-loading="status.isLoading"
      :loading-message="status.spinnerMessage"
      :disabled="status.isLoading"
      hide-reset
      @submit="submitForm"
    >
      <template #title>
        Update Home Service Order
      </template>

      <template v-if="homeServiceOrder">
        <div class="row">
          <div class="col-sm-4">
            <label class="control-label"> Phone Number </label>
            <div class="field-value">
              {{ homeServiceOrder.caller?.phoneNumber ?? '' }}
            </div>
          </div>

          <div class="col-sm-4">
            <label class="control-label">Status*</label>
            <dropdown-list
              v-model="homeServiceOrder.status"
              :options="statusList"
              required
              filterable
            />
          </div>

          <div class="col-sm-4">
            <label class="control-label"> Assigned Operator </label>
            <dropdown-list
              v-model="homeServiceOrder.operatorId"
              :options="operatorsList"
              clearable
            />
          </div>
        </div>

        <div class="row">
          <div class="col-sm-5">
            <label class="control-label"> Scheduled Follow-up Date/Time </label>
            <date-picker
              v-model="homeServiceOrder.nextFollowUpDueAt"
              type="datetime"
              placeholder="mm/dd/yyyy hh:mm A"
              format="MM/DD/YYYY hh:mm A"
              :disabled-date="disabledDate"
            />
          </div>

          <div
            v-if="!!homeServiceOrder.nextFollowUpDueAt"
            class="col-12 mt-2"
          >
            <label class="control-label"> Follow-up Reason* </label>
            <div>
              <sanitized-textarea
                v-model="homeServiceOrder.followUpReason"
                placeholder="Enter following up reason"
                autosize
                required
              />
            </div>
          </div>
        </div>

        <h6 class="sub-section-title">
          Request Details
        </h6>

        <div class="row">
          <div class="col-sm-4">
            <label class="control-label">Requested Service*</label>
            <dropdown-list
              v-model="homeServiceOrder.serviceType"
              :options="availableServiceTypeListToCreateUpdate"
              required
              filterable
            />
          </div>

          <div class="col-sm-4">
            <label class="control-label"> Job Scheduled At{{ homeServiceOrder.status === HomeServiceOrderStatus.Scheduled ? '*' : '' }} </label>
            <date-picker
              v-model="jobScheduledAtCallerLocal"
              type="datetime"
              placeholder="mm/dd/yyyy hh:mm A"
              format="MM/DD/YYYY hh:mm A"
              :disabled-date="disabledJobScheduledDate"
            />
            <small class="text-muted d-block">{{ jobScheduledAtTimeZoneLabel }}</small>
          </div>

          <div class="col-sm-4">
            <label class="control-label"> Job Ended At </label>
            <date-picker
              v-model="homeServiceOrder.jobEndedAt"
              type="date"
              placeholder="mm/dd/yyyy"
              format="MM/DD/YYYY"
              :disabled-date="disabledJobEndDate"
            />
          </div>
        </div>

        <div class="row">
          <div class="col-12 mt-2 mb-2">
            <label class="control-label"> Additional Notes </label>
            <div>
              <sanitized-textarea
                v-model="homeServiceOrder.orderNotes"
                placeholder="Enter additional notes about this service request"
                autosize
              />
            </div>
          </div>
        </div>

        <h6 class="sub-section-title">
          Service & Payment Details
        </h6>

        <div class="row">
          <div class="col-sm-6">
            <label class="control-label"> Service{{ isQuoteRequired ? '*' : '' }} </label>
            <sanitized-input
              id="homeServiceName"
              v-model="homeServiceOrder.homeServiceName"
              placeholder="Enter service name"
              :required="isQuoteRequired"
            />
          </div>

          <div class="col-sm-6">
            <label class="control-label"> Pricing Type{{ isQuoteRequired ? '*' : '' }} </label>
            <dropdown-list
              v-model="homeServiceOrder.pricingType"
              :options="pricingTypeList"
            />
          </div>
        </div>

        <div
          v-if="homeServiceOrder.pricingType === HomeServiceOrderPricingType.Hourly"
          class="row"
        >
          <div class="col-sm-6">
            <label class="control-label"> Hours{{ isQuoteRequired ? '*' : '' }} </label>
            <div>
              <el-input
                id="hours"
                v-model="homeServiceOrder.hours"
                type="number"
                step="0.01"
                :min="isQuoteRequired ? 0.01 : 0"
                :required="isQuoteRequired"
              />
            </div>
          </div>

          <div class="col-sm-6">
            <label class="control-label"> Provider Rate ($/hour){{ isQuoteRequired ? '*' : '' }} </label>
            <div>
              <el-input
                id="providerRate"
                v-model="homeServiceOrder.providerRate"
                type="number"
                step="0.01"
                min="0"
                :required="isQuoteRequired"
              >
                <template #prepend>
                  $
                </template>
              </el-input>
            </div>
          </div>
        </div>

        <div
          v-if="homeServiceOrder.pricingType === HomeServiceOrderPricingType.Fixed"
          class="row"
        >
          <div class="col-sm-6">
            <label class="control-label"> Service Price ($){{ isQuoteRequired ? '*' : '' }} </label>
            <div>
              <el-input
                id="providerRate"
                v-model="homeServiceOrder.providerRate"
                type="number"
                step="0.01"
                min="0"
                :required="isQuoteRequired"
              >
                <template #prepend>
                  $
                </template>
              </el-input>
            </div>
          </div>

          <div class="col-sm-6">
            <label class="control-label"> Estimated Duration (hours) </label>
            <div>
              <el-input
                id="estimatedDurationHours"
                v-model="homeServiceOrder.estimatedDurationHours"
                type="number"
                step="0.01"
                min="0"
              />
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-sm-4">
            <label class="control-label">
              GoGo Service Fee ({{ homeServiceOrder.pricingType === HomeServiceOrderPricingType.Hourly ? '$/hour' : '$' }}){{ isQuoteRequired ? '*' : '' }}
            </label>
            <div>
              <el-input
                id="gogoFee"
                v-model="homeServiceOrder.gogoFee"
                :placeholder="suggestedGogoFee === null ? '' : suggestedGogoFee.toFixed(2)"
                type="number"
                step="0.01"
                min="0"
                :required="isQuoteRequired"
              >
                <template #prepend>
                  $
                </template>
              </el-input>
            </div>
          </div>

          <div class="col-sm-4">
            <label class="control-label"> Finder's Fee ($) </label>
            <div>
              <el-input
                id="findersFee"
                v-model="homeServiceOrder.findersFee"
                type="number"
                step="0.01"
                min="0"
              >
                <template #prepend>
                  $
                </template>
              </el-input>
            </div>
          </div>

          <div class="col-sm-4">
            <label class="control-label"> Membership Fee ($/month) </label>
            <div>
              <el-input
                id="membershipFee"
                v-model="homeServiceOrder.membershipFee"
                type="number"
                step="0.01"
                min="0"
              >
                <template #prepend>
                  $
                </template>
              </el-input>
            </div>
          </div>
        </div>

        <div
          v-if="homeServiceOrder.pricingType"
          class="row"
        >
          <div class="col-sm-4">
            <label class="control-label"> Total Estimated Cost </label>
            <div class="field-value">
              {{ formatMoney(quoteTotal) }}
            </div>
          </div>

          <div class="col-sm-4">
            <label class="control-label"> Deposit Due (30%) </label>
            <div class="field-value">
              {{ formatMoney(quoteDepositDue) }}
            </div>
          </div>

          <div class="col-sm-4">
            <label class="control-label"> Total Estimated Cost Breakdown </label>
            <div>{{ quoteBreakdown }}</div>
          </div>
        </div>
      </template>

      <template #submit-button>
        Update
      </template>

      <template #additional-buttons>
        <p-button
          type="warning"
          round
          @click="backToList"
        >
          Back to List
        </p-button>
      </template>
    </card-container>

    <div
      v-if="homeServiceOrder"
      v-loading="status.isDuplicateOrdersLoading"
      class="page-content mb-3"
    >
      <h6>Possible Duplicates</h6>
      <p>Review the records below and mark them as duplicates if they match the primary record.</p>

      <div class="custom-collapse">
        <el-collapse
          v-model="activeCollapseId"
          accordion
        >
          <HomeServiceOrderCardItem
            v-for="item in duplicateHomeServiceOrders"
            :key="item.id"
            :order-item="item"
            :is-active="activeCollapseId === item.id"
          />
        </el-collapse>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, Ref, reactive, onMounted, defineAsyncComponent, watch, computed } from 'vue';
import { cloneDeep, isNil } from 'lodash';
import { useRoute, useRouter } from 'vue-router';
import moment from 'moment-timezone';

import util from '@/Utils/util';
import { catchErrorMessageBox } from '@/Utils/catchErrorMessageBox';
import { useSwalDialog } from '@/use/swalDialog';
import { useHomeService } from '@/use/homeService';
import { homeServiceOrderService } from '@/services/homeServiceOrder';
import { HomeServiceOrder, HomeServiceOrderStatus, type HomeServiceOrderCharge, type HomeServiceOrderProviderPayment } from '@/models/homeService';
import { HomeServiceOrderPricingType } from '@/gql/graphql';
import {
  buildQuotePayload,
  calculateDepositDue,
  calculateQuoteTotal,
  calculateSuggestedGogoFee,
  formatMoney,
  isQuoteComplete,
} from './homeServicePricing';
import { operatorService } from '@/services/operators';
import { ListType } from '@/models/app';
import { HomeServiceOrderProviderPaymentSearchType } from '@/gql/graphql';
import {
  defaultHomeServiceGogoFee,
  homeServiceOrderCancellationReasonList,
  reasonForNeedingAdditionalNotes,
} from '@/constants/homeService';
import { currentCallLog, reportCallLog } from '@/services/callLogs/callLogsViewModel';
import { useConfirmationCall } from './useConfirmationCall';

const HomeServiceOrderCardItem = defineAsyncComponent(() => import('./Order/HomeServiceOrderCardItem.vue') as any);

const pageId = 'update-home-service-order';
const operatorPaginationLimit = 100;

const route = useRoute();
const router = useRouter();
const { showSuccessPopupDialog, showConfirmPopupDialog, showConfirmPopupDialogWithValidation, showWarningPopupDialog } = useSwalDialog();
const { availableServiceTypeListToCreateUpdate, statusList } = useHomeService();

const pricingTypeList = [
  { id: 1, key: HomeServiceOrderPricingType.Hourly, label: 'Hourly' },
  { id: 2, key: HomeServiceOrderPricingType.Fixed, label: 'Fixed' },
];

const quoteTotal = computed(() =>
  homeServiceOrder.value
    ? calculateQuoteTotal({
      pricingType: homeServiceOrder.value.pricingType,
      hours: homeServiceOrder.value.hours,
      providerRate: homeServiceOrder.value.providerRate,
      gogoFee: homeServiceOrder.value.gogoFee,
      findersFee: homeServiceOrder.value.findersFee,
      membershipFee: homeServiceOrder.value.membershipFee,
    })
    : null,
);

const quoteDepositDue = computed(() => homeServiceOrder.value ? calculateDepositDue(homeServiceOrder.value) : null);
const suggestedGogoFee = computed(() => homeServiceOrder.value ? calculateSuggestedGogoFee(homeServiceOrder.value) : null);
const isQuoteRequired = computed(() =>
  homeServiceOrder.value?.status === HomeServiceOrderStatus.Scheduled &&
  (originalHomeServiceOrder.value?.status !== HomeServiceOrderStatus.Scheduled || isQuoteComplete(originalHomeServiceOrder.value)),
);

const quoteBreakdown = computed(() => {
  if (!homeServiceOrder.value?.pricingType) {
    return '—';
  }

  const quote = buildQuotePayload(homeServiceOrder.value);
  const base = homeServiceOrder.value.pricingType === HomeServiceOrderPricingType.Fixed
    ? `Service Price: ${formatMoney(quote.providerRate)} + GoGo Fee: ${formatMoney(quote.gogoFee)}`
    : `${quote.hours ?? '—'} hrs × (${formatMoney(quote.providerRate)}/hour + ${formatMoney(quote.gogoFee)}/hour GoGo fee)`;

  return `${base} + Finder’s Fee: ${formatMoney(quote.findersFee ?? 0)} + Membership Fee: ${formatMoney(quote.membershipFee ?? 0)}`;
});

const currentProviderRates = computed(() => {
  const providers = homeServiceOrder.value?.homeServiceProviders ?? [];
  const current = providers.find(provider => provider.homeServiceOrderProvider?.isCurrentProvider) ?? providers[0];
  return (current?.rates ?? []).filter(rate => rate.isActive !== false);
});

const prefillUnpricedQuoteFromProviderRate = () => {
  const order = homeServiceOrder.value;
  if (!order || order.pricingType !== HomeServiceOrderPricingType.Hourly || order.providerRate !== null && order.providerRate !== undefined) {
    return;
  }

  const applicableRates = currentProviderRates.value.filter(rate => !rate.serviceType || rate.serviceType === order.serviceType);
  if (applicableRates.length !== 1 || applicableRates[0].hourlyRate === null || applicableRates[0].hourlyRate === undefined) {
    return;
  }

  order.providerRate = applicableRates[0].hourlyRate;
};

const orderId = ref(0);
const homeServiceOrder: Ref<HomeServiceOrder | null> = ref(null);
const originalHomeServiceOrder: Ref<HomeServiceOrder | null> = ref(null);
const getConfirmationCallLogId = useConfirmationCall(homeServiceOrder, currentCallLog, reportCallLog);
const operatorsList: Ref<ListType[]> = ref([]);
const homeServiceCharges: Ref<HomeServiceOrderCharge[] | null> = ref(null);
const providerPayments: Ref<HomeServiceOrderProviderPayment[] | null> = ref(null);
const shouldSendMemberAgreementEmail = ref(false);
const duplicateHomeServiceOrders: Ref<HomeServiceOrder[]> = ref([]);
const activeCollapseId = ref(0);

const status = reactive({
  isLoading: false,
  isDuplicateOrdersLoading: false,
  spinnerMessage: '',
});

const isNeedToShowFollowUpScheduledAlert = computed(() => {
  if (!homeServiceOrder.value) {
    return false;
  }

  if ([HomeServiceOrderStatus.Canceled, HomeServiceOrderStatus.Completed].includes(homeServiceOrder.value.status)) {
    return false;
  }

  if (!homeServiceOrder.value.nextFollowUpDueAt) {
    return true;
  }

  return moment(new Date()).isAfter(moment(homeServiceOrder.value.nextFollowUpDueAt), 'day');
});

const disabledDate = (date: Date) => {
  return moment(date).isBefore(moment(), 'day');
};

const disabledJobScheduledDate = (date: Date) => {
  if (moment(date).isBefore(moment(), 'day')) {
    return true;
  }

  if (homeServiceOrder.value?.jobEndedAt && moment(date).isAfter(moment(homeServiceOrder.value?.jobEndedAt), 'day')) {
    return true;
  }

  return false;
};

const disabledJobEndDate = (date: Date) => {
  if (homeServiceOrder.value?.jobScheduledAt && moment(date).isBefore(moment(homeServiceOrder.value?.jobScheduledAt), 'day')) {
    return true;
  }

  return false;
};

const callerTimeZone = computed(() => homeServiceOrder.value?.caller?.timeZone || null);

const jobScheduledAtTimeZoneLabel = computed(() =>
  callerTimeZone.value ? `caller time: ${callerTimeZone.value}` : 'UTC — caller timezone unknown');

// The picker works with wall-clock time in the caller's timezone; the model stores UTC.
const jobScheduledAtCallerLocal = computed<Date | null>({
  get() {
    const value = homeServiceOrder.value?.jobScheduledAt;
    if (!value) {
      return null;
    }

    const zoned = callerTimeZone.value ? moment.utc(value).tz(callerTimeZone.value) : moment.utc(value);
    return new Date(zoned.year(), zoned.month(), zoned.date(), zoned.hour(), zoned.minute());
  },
  set(picked) {
    if (!homeServiceOrder.value) {
      return;
    }

    if (!picked) {
      homeServiceOrder.value.jobScheduledAt = null;
      return;
    }

    const wallTime = moment(picked).format('YYYY-MM-DD HH:mm');
    const utcValue = callerTimeZone.value
      ? moment.tz(wallTime, 'YYYY-MM-DD HH:mm', callerTimeZone.value)
      : moment.utc(wallTime, 'YYYY-MM-DD HH:mm');
    homeServiceOrder.value.jobScheduledAt = utcValue.toISOString();
  },
});

const loadHomeServiceOrder = async () => {
  const paramId = route.params.id as string;

  if (!paramId) {
    return;
  }

  status.spinnerMessage = 'Loading Home Service Order...';
  status.isLoading = true;
  return catchErrorMessageBox(async () => {
    const response = await homeServiceOrderService.getHomeServiceOrder(Number(paramId));

    if (!response) {
      return;
    }

    orderId.value = Number(paramId);
    homeServiceOrder.value = response as HomeServiceOrder;
    if (isNil(homeServiceOrder.value.gogoFee)) {
      homeServiceOrder.value.gogoFee = defaultHomeServiceGogoFee;
    }
    originalHomeServiceOrder.value = cloneDeep(homeServiceOrder.value);
  })
    .finally(() => (status.isLoading = false))
    .catch(() => (backToList()));
};

const loadDuplicateHomeServiceOrders = async () => {
  if (!homeServiceOrder.value) {
    return;
  }

  status.isDuplicateOrdersLoading = true;
  return catchErrorMessageBox(async () => {
    const statuses = statusList.value.filter(item => item.key !== HomeServiceOrderStatus.Completed).map(item => item.key);
    const response = await homeServiceOrderService.getHomeServiceOrders({
      searchType: 'callerId',
      searchValue: homeServiceOrder.value!.callerId,
      dateSearchType: '',
      serviceType: homeServiceOrder.value!.serviceType,
      status: statuses,
    });

    duplicateHomeServiceOrders.value = response.rows.filter(item => item.id !== homeServiceOrder.value!.id);
  })
    .finally(() => (status.isDuplicateOrdersLoading = false))
    .catch(() => null);
};

const backToList = () => {
  router.push({
    name: 'HomeServiceOrders',
  });
};

const loadShouldSendMemberAgreementEmail = async () => {
  if (!homeServiceOrder.value?.callerId) {
    return;
  }

  shouldSendMemberAgreementEmail.value = await homeServiceOrderService.shouldSendHomeServicesMemberAgreementEmail(
    homeServiceOrder.value.callerId,
  );
};

const loadHomeServiceOperators = async (currentPage: number = 1) => {
  const offset = (currentPage - 1) * operatorPaginationLimit;

  status.spinnerMessage = 'Loading Home Service Operators...';
  status.isLoading = true;
  try {
    const response = await operatorService.fetchOperators({
      roles: ['homeServices-operator'],
      isActive: true,
      pagination: {
        offset,
        limit: operatorPaginationLimit,
      },
    });

    for (const operator of response.rows) {
      operatorsList.value.push({
        id: operator.id,
        key: operator.id,
        label: `${operator.firstName} ${operator.lastName}`,
      });
    }

    if (operatorsList.value.length < response.count) {
      return loadHomeServiceOperators(currentPage + 1);
    }
  } catch (err) {
    // No action needed because this runs as a background process.
  } finally {
    status.isLoading = false;
  }
};

const checkToUpdateStatus = async (): Promise<{
  isAllowToUpdate: boolean;
  cancellationReason?: string;
}> => {
  const canceledValidation  = await validateCanceledStatus();

  if (canceledValidation) {
    return canceledValidation ;
  }

  status.spinnerMessage = 'Loading Home Service Order Charges...';
  status.isLoading = true;
  const completedValidation  = await validateCompletedStatus();
  status.isLoading = false;

  if (completedValidation ) {
    return completedValidation ;
  }

  return {
    isAllowToUpdate: true,
  }
};

const validateCanceledStatus = async () => {
  const isCancelingOrder = homeServiceOrder.value?.status === HomeServiceOrderStatus.Canceled && originalHomeServiceOrder.value?.status !== HomeServiceOrderStatus.Canceled;
  if (!isCancelingOrder) {
    return;
  }

  const cancellationReason = await getCancellationReason();

  return {
    isAllowToUpdate: Boolean(cancellationReason),
    cancellationReason,
  };
};

const getCancellationReason = async () => {
  const reasons = homeServiceOrderCancellationReasonList.reduce(
    (
      obj: {
        [key: string]: string;
      },
      entry: ListType,
    ) => {
      obj[entry.key] = entry.label;

      return obj;
    },
    {},
  );

  const cancellationReason = await showConfirmPopupDialogWithValidation({
    text: 'Are you sure you want to cancel this order?',
    title: 'Cancel order?',
    input: 'select',
    inputOptions: reasons,
    inputPlaceholder: 'Please select cancellation reason',
    validationText: 'Please select cancellation reason',
    confirmButtonText: 'Yes, Cancel',
  });

  if (!cancellationReason) {
    return;
  }

  if (!reasonForNeedingAdditionalNotes.includes(cancellationReason)) {
    return cancellationReason;
  }

  const additionalNotes = await showConfirmPopupDialogWithValidation({
    text: 'Please indicate the additional notes to cancel the order',
    title: 'Cancel order?',
    input: 'textarea',
    inputPlaceholder: 'Please input additional notes',
    validationText: 'Please input additional notes',
    confirmButtonText: 'Yes, Cancel',
  });

  if (!additionalNotes) {
    return;
  }

  return `${cancellationReason} - ${additionalNotes}`;
};

const validateCompletedStatus = async () => {
  const isCompletingOrder = homeServiceOrder.value?.status === HomeServiceOrderStatus.Completed && originalHomeServiceOrder.value?.status !== HomeServiceOrderStatus.Completed;
  if (!isCompletingOrder) {
    return;
  }

  await fetchHomeServiceOrderCharges();

  if (!homeServiceCharges.value?.length) {
    await showWarningPopupDialog({
      text: 'Please add the order charges before completing the order.',
    });

    return {
      isAllowToUpdate: false,
    };
  }

  await fetchHomeServiceOrderProviderPayments();

  if (!providerPayments.value?.length) {
    await showWarningPopupDialog({
      text: 'Please add the provider payments before completing the order.',
    });

    return {
      isAllowToUpdate: false,
    };
  }

  return {
    isAllowToUpdate: true,
  }
};

const fetchHomeServiceOrderCharges = async () => {
  if (homeServiceCharges.value) {
    return;
  }

  try {
    homeServiceCharges.value = await homeServiceOrderService.getHomeServiceOrderCharges(homeServiceOrder.value!.id);
  } catch (err) {
    homeServiceCharges.value = [];
  }
};

const fetchHomeServiceOrderProviderPayments = async () => {
  if (providerPayments.value) {
    return;
  }

  try {
    const response = await homeServiceOrderService.getHomeServiceOrderProviderPayments({
      searchType: HomeServiceOrderProviderPaymentSearchType.HomeServiceOrderId,
      searchValue: homeServiceOrder.value!.id.toString(),
      pagination: {
        offset: 0,
        limit: 100,
      }
    });

    providerPayments.value = response.rows.map(item => item as HomeServiceOrderProviderPayment);
  } catch (err) {
    providerPayments.value = [];
  }
};

const confirmFollowUpScheduledAlert = async () => {
  if (!isNeedToShowFollowUpScheduledAlert.value) {
    return true;
  }

  return showConfirmPopupDialog({
    title: 'You haven\'t scheduled a follow-up',
    text: [
      'You\'re about to save this order without a follow-up date.',
      'Would you like to go back and schedule one?'
    ].join('<br>'),
    confirmButtonText: 'Save Order',
    cancelButtonText: 'Edit Scheduled Follow-up',
    type: 'html',
  });
}

const submitForm = async () => {
  if (!homeServiceOrder.value) {
    return;
  }

  const isStatusChangingToScheduled =
    originalHomeServiceOrder.value?.status !== HomeServiceOrderStatus.Scheduled &&
    homeServiceOrder.value.status === HomeServiceOrderStatus.Scheduled;

  let sendMemberPoliciesEmail = false;

  if (isStatusChangingToScheduled && shouldSendMemberAgreementEmail.value) {
    sendMemberPoliciesEmail = await showConfirmPopupDialog({
      title: 'Send Member Policies Email',
      text: 'Would you like the Member Policies email to be automatically sent to this caller?',
      confirmButtonText: 'Yes, send email',
      cancelButtonText: 'No, do not send email',
    });
  }

  catchErrorMessageBox(async () => {
    const isConfirmed = await confirmFollowUpScheduledAlert();
    if (!isConfirmed) {
      return;
    }

    const valid = await checkToUpdateStatus();

    if (!valid.isAllowToUpdate) {
      return;
    }

    const payload = {
      callLogId: getConfirmationCallLogId(isStatusChangingToScheduled),
      status: homeServiceOrder.value?.status,
      operatorId: homeServiceOrder.value?.operatorId || null, // avoid sending empty string
      nextFollowUpDueAt: homeServiceOrder.value?.nextFollowUpDueAt && util.formatDateTime(homeServiceOrder.value!.nextFollowUpDueAt, 'YYYY-MM-DD HH:mm'),
      followUpReason: homeServiceOrder.value?.nextFollowUpDueAt ? homeServiceOrder.value?.followUpReason : null,
      serviceType: homeServiceOrder.value?.serviceType,
      jobScheduledAt: homeServiceOrder.value?.jobScheduledAt ?? null,
      jobEndedAt: homeServiceOrder.value?.jobEndedAt ? util.formatDateTime(homeServiceOrder.value.jobEndedAt, 'YYYY-MM-DD HH:mm') : null,
      orderNotes: homeServiceOrder.value?.orderNotes,
      cancellationReason: valid.cancellationReason ?? null,
      homeServiceName: homeServiceOrder.value?.homeServiceName ?? null,
      pricingType: homeServiceOrder.value?.pricingType ?? null,
      ...buildQuotePayload({
        pricingType: homeServiceOrder.value?.pricingType,
        hours: homeServiceOrder.value?.hours,
        providerRate: homeServiceOrder.value?.providerRate,
        gogoFee: homeServiceOrder.value?.gogoFee,
        findersFee: homeServiceOrder.value?.findersFee,
        membershipFee: homeServiceOrder.value?.membershipFee,
        estimatedDurationHours: homeServiceOrder.value?.estimatedDurationHours,
      }),
    };

    status.spinnerMessage = 'Updating Home Service Order...';
    status.isLoading = true;

    await homeServiceOrderService.updateHomeServiceOrder({
      id: homeServiceOrder.value!.id,
      data: payload,
      sendMemberPoliciesEmail,
    });

    await showSuccessPopupDialog({
      text: 'Service order has been updated successfully!',
    });

    if (isStatusChangingToScheduled) {
      backToList();
      return;
    }

    originalHomeServiceOrder.value = cloneDeep(homeServiceOrder.value);
  })
    .finally(() => (status.isLoading = false))
    .catch(() => null);
};

const fetchOrderData = async () => {
  util.moveToTop();
  homeServiceOrder.value = null;
  originalHomeServiceOrder.value = null;
  duplicateHomeServiceOrders.value = [];

  await loadHomeServiceOrder();
  await loadDuplicateHomeServiceOrders();
  await loadShouldSendMemberAgreementEmail();
};

watch(() => route.params.id, fetchOrderData);
watch(() => homeServiceOrder.value?.pricingType, prefillUnpricedQuoteFromProviderRate);

onMounted(async () => {
  await loadHomeServiceOperators();
  await fetchOrderData();
});
</script>

<style lang="scss">
#update-home-service-order {
  input[type='number'] {
    max-width: unset;
  }

  .field-value {
    color: #e3210a;
  }
}
</style>